import type { BlendMode, Id, ScaleFilter, SceneItem, Vec2 } from '../../core/model';
import type { StudioApi } from '../../engine/api';
import type { FilterOwner, FitMode } from '../../state/api';
import { editedSceneIdOf } from '../context';
import { attempt } from '../safe';
import type { UiStore } from '../uiStore';

export interface ItemActions {
  editedSceneId(): Id | null;
  /** Selection restricted to items that still exist in the edited scene. */
  selectedIds(): Id[];
  findItem(itemId: Id): SceneItem | null;
  contentSize(itemId: Id): Vec2;
  filterOwnerOf(itemId: Id): FilterOwner | null;

  selectAll(): void;
  removeItems(ids?: readonly Id[]): void;
  duplicate(mode: 'reference' | 'copy', ids?: readonly Id[]): void;
  copyItems(): void;
  pasteItems(): void;
  copyTransform(): void;
  pasteTransform(): void;
  copyFilters(): void;
  pasteFilters(): void;
  nudge(dx: number, dy: number): void;
  fit(mode: FitMode): void;
  flip(axis: 'horizontal' | 'vertical'): void;
  rotate(degrees: number): void;
  order(direction: 'up' | 'down' | 'top' | 'bottom'): void;
  group(): void;
  ungroup(): void;
  toggleVisibility(): void;
  toggleLock(): void;
  setBlendMode(mode: BlendMode): void;
  setScaleFilter(filter: ScaleFilter): void;
  openProperties(itemId?: Id): void;
  openFilters(itemId?: Id): void;
  openTransform(itemId?: Id): void;
}

export function createItemActions(studio: StudioApi, ui: UiStore): ItemActions {
  const { commands, queries } = studio;
  const project = () => studio.project.getState().project;
  const sceneId = (): Id | null => editedSceneIdOf(studio.runtime.getState());
  const run = <T>(work: () => T): T | undefined => attempt(studio, work);

  const find = (itemId: Id): SceneItem | null => {
    const scene = sceneId();
    return scene ? (queries.findItem(project(), scene, itemId)?.item ?? null) : null;
  };

  const selectedIds = (): Id[] => ui.getState().selection.filter((id) => find(id) !== null);

  /** Runs `work` once per selected item inside the edited scene. */
  const eachSelected = (work: (scene: Id, itemId: Id) => void): void => {
    const scene = sceneId();
    if (!scene) return;
    run(() => {
      for (const id of selectedIds()) work(scene, id);
    });
    studio.project.sealHistory();
  };

  const size = (itemId: Id): Vec2 => {
    const scene = sceneId();
    if (!scene) return { x: 0, y: 0 };
    try {
      return studio.getItemContentSize(scene, itemId);
    } catch {
      return { x: 0, y: 0 };
    }
  };

  const ownerOf = (itemId: Id): FilterOwner | null => {
    const item = find(itemId);
    if (!item) return null;
    if (item.type === 'source') return { type: 'source', id: item.sourceId };
    if (item.type === 'scene') return { type: 'scene', id: item.sceneId };
    return null;
  };

  const filtersOf = (owner: FilterOwner) =>
    owner.type === 'source' ? project().sources[owner.id]?.filters : project().scenes[owner.id]?.filters;

  const doRemove = (scene: Id, ids: readonly Id[]): void => {
    run(() => commands.removeItems(scene, ids));
    ui.getState().select([]);
  };

  return {
    editedSceneId: sceneId,
    selectedIds,
    findItem: find,
    contentSize: size,
    filterOwnerOf: ownerOf,

    selectAll() {
      const scene = sceneId();
      const data = scene ? project().scenes[scene] : undefined;
      if (data) ui.getState().select(data.items.filter((i) => !i.locked).map((i) => i.id));
    },

    removeItems(ids) {
      const scene = sceneId();
      const targets = ids ? [...ids] : selectedIds();
      if (!scene || targets.length === 0) return;
      if (!ui.getState().prefs.confirmRemoveItem) {
        doRemove(scene, targets);
        return;
      }
      ui.getState().openDialog({
        type: 'confirm',
        title: 'Remove items',
        message: targets.length === 1 ? 'Remove the selected item?' : `Remove ${targets.length} selected items?`,
        confirmLabel: 'Remove',
        danger: true,
        onConfirm: () => doRemove(scene, targets),
      });
    },

    duplicate(mode, ids) {
      const scene = sceneId();
      const targets = ids ? [...ids] : selectedIds();
      if (!scene || targets.length === 0) return;
      const created = run(() => commands.duplicateItems(scene, targets, mode));
      if (created) ui.getState().select(created);
    },

    copyItems() {
      const scene = sceneId();
      const itemIds = selectedIds();
      if (scene && itemIds.length > 0) ui.getState().setClipboard({ items: { sceneId: scene, itemIds } });
    },

    pasteItems() {
      const scene = sceneId();
      const clip = ui.getState().clipboard.items;
      if (!scene || !clip) return;
      const current = project();
      const sourceScene = current.scenes[clip.sceneId];
      if (!sourceScene) return;
      const alive = clip.itemIds.filter((id) => queries.findItem(current, clip.sceneId, id));
      if (alive.length === 0) return;
      if (clip.sceneId === scene) {
        const created = run(() => commands.duplicateItems(scene, alive, 'reference'));
        if (created) ui.getState().select(created);
        return;
      }
      const created: Id[] = [];
      let skippedGroups = 0;
      run(() => {
        for (const id of alive) {
          const item = queries.findItem(current, clip.sceneId, id)!.item;
          const options = { transform: item.transform, visible: item.visible };
          if (item.type === 'source') created.push(commands.addExistingSource(scene, item.sourceId, options));
          else if (item.type === 'scene') created.push(commands.addNestedScene(scene, item.sceneId, options));
          else skippedGroups++;
        }
      });
      if (skippedGroups > 0) studio.notify('warn', 'Groups can only be pasted into the scene they were copied from.');
      if (created.length > 0) ui.getState().select(created);
    },

    copyTransform() {
      const item = find(selectedIds()[0] ?? '');
      if (item) ui.getState().setClipboard({ transform: structuredClone(item.transform) });
    },

    pasteTransform() {
      const transform = ui.getState().clipboard.transform;
      if (transform) eachSelected((scene, id) => commands.setItemTransform(scene, id, structuredClone(transform)));
    },

    copyFilters() {
      const owner = ownerOf(selectedIds()[0] ?? '');
      const filters = owner ? filtersOf(owner) : undefined;
      if (filters) ui.getState().setClipboard({ filters: structuredClone(filters) as typeof filters });
    },

    pasteFilters() {
      const filters = ui.getState().clipboard.filters;
      if (!filters) return;
      run(() => {
        for (const id of selectedIds()) {
          const owner = ownerOf(id);
          if (!owner) continue;
          for (const filter of filters) {
            const created = commands.addFilter(owner, filter.kind, filter.name);
            commands.updateFilter(owner, created, { enabled: filter.enabled, settings: structuredClone(filter.settings) });
          }
        }
      });
    },

    nudge(dx, dy) {
      const scene = sceneId();
      if (!scene) return;
      run(() => {
        for (const id of selectedIds()) {
          const item = find(id);
          if (!item || item.locked) continue;
          const { position } = item.transform;
          commands.setItemTransform(
            scene,
            id,
            { position: { x: position.x + dx, y: position.y + dy } },
            { coalesceKey: `nudge:${id}` },
          );
        }
      });
    },

    fit: (mode) =>
      eachSelected((scene, id) =>
        commands.fitItem(scene, id, mode, size(id), (itemId) => studio.getItemContentSize(scene, itemId)),
      ),
    flip: (axis) => eachSelected((scene, id) => commands.flipItem(scene, id, axis)),
    rotate: (degrees) => eachSelected((scene, id) => commands.rotateItem(scene, id, degrees)),
    order: (direction) => eachSelected((scene, id) => commands.orderItem(scene, id, direction)),

    group() {
      const scene = sceneId();
      const ids = selectedIds();
      if (!scene || ids.length === 0) return;
      const groupId = run(() => commands.groupItems(scene, ids, undefined, (id) => studio.getItemContentSize(scene, id)));
      if (groupId) ui.getState().select([groupId]);
    },

    ungroup() {
      const scene = sceneId();
      if (!scene) return;
      run(() => {
        for (const id of selectedIds()) if (find(id)?.type === 'group') commands.ungroup(scene, id, (itemId) => studio.getItemContentSize(scene, itemId));
      });
      ui.getState().select([]);
    },

    toggleVisibility() {
      const first = find(selectedIds()[0] ?? '');
      if (first) eachSelected((scene, id) => commands.updateItem(scene, id, { visible: !first.visible }));
    },

    toggleLock() {
      const first = find(selectedIds()[0] ?? '');
      if (first) eachSelected((scene, id) => commands.updateItem(scene, id, { locked: !first.locked }));
    },

    setBlendMode: (blendMode) => eachSelected((scene, id) => commands.updateItem(scene, id, { blendMode })),
    setScaleFilter: (scaleFilter) => eachSelected((scene, id) => commands.updateItem(scene, id, { scaleFilter })),

    openProperties(itemId) {
      const item = find(itemId ?? selectedIds()[0] ?? '');
      if (item?.type === 'source') ui.getState().openDialog({ type: 'properties', sourceId: item.sourceId });
    },

    openFilters(itemId) {
      const owner = ownerOf(itemId ?? selectedIds()[0] ?? '');
      if (owner) ui.getState().openDialog({ type: 'filters', owner });
    },

    openTransform(itemId) {
      const scene = sceneId();
      const id = itemId ?? selectedIds()[0];
      if (scene && id && find(id)) ui.getState().openDialog({ type: 'transform', sceneId: scene, itemId: id });
    },
  };
}
